import { type ReactElement, type Ref, useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useTheme } from 'next-themes';
import {
  type ColDef,
  type ColGroupDef,
  type GridOptions,
  type GridApi,
  type ICellRendererParams,
  type IRowNode,
  type RowDataUpdatedEvent,
  type SelectionChangedEvent,
  type ValueFormatterParams,
  type ITextFilterParams,
  type RowClickedEvent,
  type RowDragEndEvent,
  type RowDragMoveEvent,
  type RowDragLeaveEvent,
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  iconSetQuartzBold,
} from 'ag-grid-community';

import { cn } from '@outmarket/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { resolveGridThemeColors } from './gridThemeTokens';

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

export type TDataTableSize = 'sm' | 'default' | 'md' | 'lg';

type TDataTableVariant = 'bordered' | 'borderless' | 'horizontal';

/**
 * Size variants for the table with corresponding row heights
 */
const dataTableVariants = cva('', {
  variants: {
    size: {
      sm: '',
      default: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

/**
 * Get row height based on size variant.
 *
 * Exported because consumers that compute their own deterministic grid height
 * (the pivot sizes its viewport to exactly its content so the pinned totals row
 * sits flush) need the SAME number the theme hands AG Grid — a mirrored literal
 * drifts the moment a size variant changes.
 */
export function getGridRowHeight(size: TDataTableSize): number {
  switch (size) {
    case 'sm':
      return 40;
    case 'md':
      return 72;
    case 'lg':
      return 96;
    case 'default':
    default:
      return 52;
  }
}

/**
 * The grid theme's DEFAULT header-band height — what a consumer renders unless it
 * passes its own `headerHeight` (SharedItemsTable, for one, overrides it with 48).
 * It is the band the raw-data grid, the flat KPI table and the pivot's leaf header
 * all take, which is what makes table widgets side by side on a dashboard line up.
 * Exported for the same reason as `getGridRowHeight`: a consumer computing its own
 * grid height must read the value rather than mirror the literal.
 */
export const GRID_HEADER_HEIGHT = 56;

/**
 * Builds the plain `themeQuartz.withParams(...)` parameter object for the grid,
 * resolving a light/dark hex palette from the active theme (AG-Grid's theming
 * API takes JS values, not CSS vars). Extracted as a PURE function — separate
 * from the opaque `Theme` object `withParams` returns — so the generated params
 * (notably the body-parented filter-popup input/border/button styling, which
 * can't be asserted through scoped CSS) are directly unit-testable.
 */
export function buildGridThemeParams(
  size: TDataTableSize,
  variant: TDataTableVariant = 'borderless',
  background?: string,
  isDark = false,
  striped = false,
) {
  const rowHeight = getGridRowHeight(size);

  // Light/dark token pairs — kept in sync with the .dark CSS vars in
  // apps/web-ui/app/globals.scss so AG-Grid surfaces match the rest of the
  // app. Cell text / borders / row hover all flip together.
  const foregroundColor = isDark ? '#E0E0E0' : '#0a0a0a';
  const mutedForegroundColor = isDark ? '#B3B3B3' : '#737373';
  const borderColor = isDark ? '#333333' : '#e5e5e5';
  const mutedBackgroundColor = isDark ? '#272727' : '#f5f5f5';
  const backgroundColor = isDark ? '#272727' : '#ffffff';
  const cardBackgroundColor = isDark ? '#272727' : '#ffffff';
  // Header band + zebra-stripe fills come from the shared grid token (same
  // light/dark pairs the native header + the pivot's sticky rails use), so
  // nothing re-hardcodes them.
  const gridThemeColors = resolveGridThemeColors(isDark ? 'dark' : 'light');
  const headerBackground = gridThemeColors.headerShade;
  // rowHover is a DIFFERENT pair in dark (#333333, not the header shade), so it
  // stays its own literal rather than the shared token. In light it is
  // neutral07 (#F2F4F7), one step darker than the neutral08 stripe — hovering a
  // striped row has to change something, and the old #F9FAFB hover was the
  // stripe colour exactly, so every other row would have hovered invisibly.
  const rowHover = isDark ? '#333333' : '#F2F4F7';

  // Brand primary flips purple → orange in dark; foreground on primary
  // becomes near-black for accessible contrast on the orange.
  const primaryColor = isDark ? '#FF5722' : '#6A56F1';
  const primaryForegroundColor = isDark ? '#1a1a1a' : '#f5f5f7';
  const radiusValue = 2;

  // ── Filter-popup field styling tokens ──
  // The per-column filter popup (agTextColumnFilter / agNumberColumnFilter), the
  // ⋮ column menu, cell editors and the page-size picker are parented to
  // <body>, so scoped CSS on the grid wrapper can't reach them — they're themed
  // here. These derive from the isDark-flipped literals above so both themes
  // read correctly.
  //
  // ~6px corner radius to match our design-system `rounded-md` inputs / selects
  // / popovers (NOT the grid's tight 2px checkbox radius).
  const inputRadiusValue = 6;
  // Focus ring derived from the brand primary (same purple→orange flip as
  // `primaryColor`), at ~35% opacity so the 2px halo reads on both the light and
  // dark popup surfaces — approximates our Input's `focus:ring-1 ring-primary`.
  const primaryFocusRing = isDark ? 'rgba(255, 87, 34, 0.35)' : 'rgba(106, 86, 241, 0.35)';
  // Popup drop shadow (~`shadow-md`). Both are pure-black alphas (no hue flip),
  // stronger in dark so it still reads against the dark card surface.
  const popupShadowValue = {
    offsetY: 4,
    radius: 12,
    spread: -2,
    color: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.14)',
  };

  // Configure borders based on variant
  const borderConfig = (() => {
    switch (variant) {
      case 'bordered':
        return {
          wrapperBorder: true,
          rowBorder: true,
          columnBorder: true,
          headerRowBorder: true,
          headerColumnBorder: true,
          // themeQuartz defaults this divider to a 50%-height tick; force it
          // full-height so header separators are continuous lines that line up
          // with the body columnBorder, not short center ticks.
          headerColumnBorderHeight: '100%',
          // themeQuartz paints a short rounded resize grip (borderColor, ~30%
          // height) at every resizable column's right edge — a grey "strip"
          // that doubles the full-height border. Hide it (resize still works via
          // the invisible hit area) so only the clean border line shows.
          headerColumnResizeHandleColor: 'transparent',
          // Gently round the outer frame (overrides the global 0 below). Only
          // the self-framed bordered variant rounds — borderless tables that
          // sit inside their own container must stay square to avoid a corner
          // gap.
          wrapperBorderRadius: 8,
        };
      case 'horizontal':
        // Data-first look: horizontal row rules only. No outer frame, no vertical
        // column separators — the consuming widget card frames the table. The
        // header keeps its bottom hairline (headerRowBorder) on the header-shade
        // fill. Used by the pivot; 'bordered'/'borderless' are untouched so Vault
        // and other consumers are unaffected.
        return {
          wrapperBorder: false,
          rowBorder: true,
          columnBorder: false,
          headerRowBorder: true,
          headerColumnBorder: false,
          wrapperBorderRadius: 0,
        };
      case 'borderless':
      default:
        return {
          wrapperBorder: false,
          rowBorder: false,
          headerRowBorder: false,
        };
    }
  })();

  // Use custom background if provided, otherwise use default
  const tableBackgroundColor = background || backgroundColor;

  return {
    // Background colors
    backgroundColor: tableBackgroundColor,
    // Zebra striping is opt-in (`striped`): odd rows take the shared stripe
    // shade, even rows the body fill. Opted out, both are the body fill — i.e.
    // AG-Grid's own alternating default stays suppressed, as before.
    oddRowBackgroundColor: striped ? gridThemeColors.stripeShade : tableBackgroundColor,
    headerBackgroundColor: headerBackground,
    filterPanelApplyButtonBackgroundColor: primaryColor,

    // Text colors
    foregroundColor: foregroundColor,
    cellTextColor: foregroundColor,
    headerTextColor: foregroundColor,
    // Pivot (horizontal variant) matches the flat tabular widget's lighter 500
    // header; every other DataTableV2 consumer (Vault, forms, epicView, market,
    // ingestion) keeps the standard 600.
    headerFontWeight: variant === 'horizontal' ? 500 : 600,

    // Accent color (controls hover state)
    accentColor: mutedBackgroundColor,
    rowHoverColor: rowHover,

    // Border color
    borderColor: borderColor,

    // No internal rounding on the AG-Grid wrapper — the consuming component
    // (e.g. AssetsView in Vault) already wraps the grid in its own bordered
    // container; the Quartz default ~8px inside radius leaves a visible gap
    // between the outer wrapper and the inner grid corners.
    wrapperBorderRadius: 0,

    // Border configuration based on variant
    ...borderConfig,

    // Icon button colors
    iconButtonActiveColor: foregroundColor,
    iconButtonActiveIndicatorColor: primaryColor,

    // Menu styling
    menuBackgroundColor: cardBackgroundColor,

    // Typography — match the rest of the app (Inter), which is loaded in the
    // web-ui root layout via next/font and exposed as `--font-primary`. The old
    // `--font-sans` variable is defined nowhere, so ag-grid fell back to its
    // built-in default font stack and looked out of place next to every other
    // dashboard widget. The `Inter, sans-serif` fallback keeps it correct in
    // contexts (e.g. Storybook) where the body variable isn't present.
    fontFamily: 'var(--font-primary, Inter, sans-serif)',
    headerFontFamily: 'var(--font-primary, Inter, sans-serif)',

    // Font sizes
    fontSize: 14,
    headerFontSize: 14,

    // Spacing and padding
    cellHorizontalPaddingScale: 1.2,
    rowVerticalPaddingScale: 1.1,
    headerVerticalPaddingScale: 1.2,

    // Icon size
    iconSize: 14,

    // Browser color scheme detection
    browserColorScheme: isDark ? 'dark' : 'light',

    // Control heights
    headerHeight: GRID_HEADER_HEIGHT,
    rowHeight: rowHeight,
    inputHeight: 36,
    listItemHeight: 36,

    // Checkbox styling
    checkboxBorderRadius: radiusValue,
    checkboxBorderWidth: 1,
    checkboxCheckedBackgroundColor: primaryColor,
    checkboxCheckedBorderColor: primaryColor,
    checkboxCheckedShapeColor: primaryForegroundColor,
    checkboxIndeterminateBackgroundColor: primaryColor,
    checkboxIndeterminateBorderColor: primaryColor,
    checkboxIndeterminateShapeColor: primaryForegroundColor,
    checkboxUncheckedBackgroundColor: 'transparent',
    /* mutedForegroundColor is a mid-tone gray — visible on both card-white
       (light) and card-dark (dark) surfaces. inputColor blends into the
       card bg, making the unchecked checkbox effectively invisible. */
    checkboxUncheckedBorderColor: mutedForegroundColor,

    // ── Filter popup + menu styling ──
    // Give the body-parented filter popup (operator dropdown, value input, Clear
    // button) and the ⋮ column menu real boxes that approximate our design
    // system. Without these the popup's fields render borderless and off-brand.

    // General widget corner radius → rounds the popup/menu card and the operator
    // dropdown (a picker button, which has no radius param of its own) to
    // ~rounded-md. Checkboxes keep their 2px via checkboxBorderRadius above and
    // the outer frame keeps wrapperBorderRadius: 0, so only popup-family widgets
    // pick this up.
    borderRadius: inputRadiusValue,

    // Text / number inputs ("Filter…") — visible border, readable fill, rounded
    // corners, left padding, muted placeholder and a primary focus border + ring
    // (≈ our Input: border-neutral05, bg-background, rounded-md, focus ring-1
    // ring-primary). inputHeight (36) is already set above.
    inputBackgroundColor: tableBackgroundColor,
    inputBorder: { color: borderColor, width: 1 },
    inputBorderRadius: inputRadiusValue,
    inputPaddingStart: 8,
    inputTextColor: foregroundColor,
    inputPlaceholderTextColor: mutedForegroundColor,
    inputFocusBorder: { color: primaryColor, width: 1 },
    inputFocusShadow: { spread: 2, color: primaryFocusRing },

    // Operator dropdown ("Equals ▾") — an ag-grid picker button; match the
    // inputs' border / fill / primary focus border so it reads as a boxed Select,
    // and give its dropdown list the same bordered card as the popup.
    pickerButtonBackgroundColor: tableBackgroundColor,
    pickerButtonBorder: { color: borderColor, width: 1 },
    pickerButtonFocusBorder: { color: primaryColor, width: 1 },
    pickerListBackgroundColor: cardBackgroundColor,
    pickerListBorder: { color: borderColor, width: 1 },

    // The popup / menu card itself — border + drop shadow so it reads as a
    // popover (≈ our Popover: rounded-md border bg-popover shadow-md).
    menuBorder: { color: borderColor, width: 1 },
    menuShadow: popupShadowValue,
    popupShadow: popupShadowValue,

    // The per-column Clear button (relabeled from "Reset" via localeText on the
    // flat KPI table) and any other standard filter action button — styled as our
    // outline / secondary Button: border, rounded-md corners, comfortable
    // padding, subtle hover fill. (The Filters Tool Panel apply button keeps its
    // primary fill via filterPanelApplyButtonBackgroundColor above.)
    buttonBackgroundColor: tableBackgroundColor,
    buttonTextColor: foregroundColor,
    buttonBorder: { color: borderColor, width: 1 },
    buttonBorderRadius: inputRadiusValue,
    buttonHorizontalPadding: 12,
    buttonVerticalPadding: 6,
    buttonHoverBackgroundColor: mutedBackgroundColor,
    buttonHoverBorder: { color: borderColor, width: 1 },
    buttonHoverTextColor: foregroundColor,
  };
}

/**
 * Creates a themed AG Grid configuration using hardcoded design tokens. Wraps
 * the pure `buildGridThemeParams` in `themeQuartz.withPart(...).withParams(...)`.
 */
function createThemedGridConfig(
  size: TDataTableSize,
  variant: TDataTableVariant = 'borderless',
  background?: string,
  isDark = false,
  striped = false,
) {
  return themeQuartz
    .withPart(iconSetQuartzBold)
    .withParams(buildGridThemeParams(size, variant, background, isDark, striped));
}

/**
 * `AgGridReact`, re-typed for JSX at this one boundary. ag-grid-react's class is
 * typed against the root React 19 types, whose `Component` dropped `refs`; this
 * package type-checks JSX against its own React 18 types, whose `Component`
 * requires it — so the class is "not a valid JSX element type" here even though
 * it renders fine at runtime. Same props + ref it really accepts; no runtime
 * change (it is the same class).
 */
const AgGrid = AgGridReact as unknown as <T>(
  props: AgGridReactProps<T> & { ref?: Ref<AgGridReact<T>> },
) => ReactElement;

/**
 * Extends the AgGridReact props with DataTableV2 props.
 *
 * Typed from ag-grid's own `AgGridReactProps`, NOT `ComponentProps<typeof
 * AgGridReact>`: packages/ui type-checks against its own React 18 types while
 * ag-grid-react resolves the root React 19 types, so `ComponentProps` of the
 * class fails React 18's JSXElementConstructor check and collapses to `{}` —
 * silently dropping every ag-grid prop (pinnedBottomRowData, gridOptions,
 * rowSelection, …) from DataTableV2. `AgGridReactProps` is the same props
 * interface without depending on which React types are in scope.
 */
interface IDataTableV2Props<TData = unknown>
  extends Omit<AgGridReactProps<TData>, 'rowData' | 'columnDefs'>,
    VariantProps<typeof dataTableVariants> {
  data: TData[];
  // Accept two-level headers (a KPI group over its period columns) as well as
  // flat columns. `ColDef[]` is still assignable, so every existing caller is
  // unaffected.
  columns: (ColDef<TData> | ColGroupDef<TData>)[];
  defaultColDef?: ColDef<TData>;
  className?: string;
  resizable?: boolean;
  size?: TDataTableSize;
  variant?: TDataTableVariant;
  background?: string;
  /**
   * Zebra-stripe alternating body rows (odd rows take the shared stripe shade).
   * Opt-in: tables that frame each row some other way (Vault, the forms list,
   * the market list) stay flat.
   */
  striped?: boolean;
  /** Controlled selection: array of row IDs (matched via getRowId) that are currently selected */
  selectedRows?: string[];
  /** Maximum allowed selections. When reached, unselected row checkboxes are disabled. */
  maxSelections?: number;
  /** Callback with updated selected row IDs after every selection change (limit-enforced). */
  onSelectionChange?: (selectedRowIds: string[]) => void;
  /** Called when a selection attempt is clamped by maxSelections. Use to show user feedback. */
  onMaxSelectionsReached?: (maxSelections: number) => void;
}

function DataTableV2<TData = unknown>({
  data,
  columns,
  defaultColDef,
  gridOptions,
  className,
  size = 'default',
  variant = 'borderless',
  resizable = false,
  background,
  striped = false,
  selectedRows,
  maxSelections,
  onSelectionChange: onSelectionChangeProp,
  onMaxSelectionsReached: onMaxSelectionsReachedProp,
  rowSelection: consumerRowSelection,
  onSelectionChanged: consumerOnSelectionChanged,
  domLayout,
  ...restProps
}: IDataTableV2Props<TData>) {
  const gridRef = useRef<AgGridReact<TData>>(null);
  const isControlledSelection = selectedRows !== undefined;
  const selectedRowsSet = useMemo(() => new Set(selectedRows ?? []), [selectedRows]);

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const themedConfig = useMemo(
    () => createThemedGridConfig(size, variant, background, isDark, striped),
    [size, variant, background, isDark, striped],
  );

  const mergedGridOptions: GridOptions<TData> = useMemo(
    () => ({
      theme: themedConfig,
      animateRows: false,
      suppressCellFocus: true,
      defaultColDef: {
        resizable: resizable,
        flex: 1,
        ...defaultColDef,
      },
      ...(gridOptions || {}),
    }),
    [themedConfig, gridOptions, resizable, defaultColDef],
  );

  // AG-Grid's `gridOptions` prop is read once at grid creation; subsequent
  // changes (including a new theme object) don't propagate to the running
  // grid. Push the freshly-built theme onto the live grid imperatively so the
  // table re-skins immediately when next-themes flips dark/light instead of
  // requiring a page refresh.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setGridOption('theme', themedConfig);
  }, [themedConfig]);

  // ── Controlled selection: sync selectedRows prop → AG Grid ──

  const syncSelectionToGrid = useCallback(
    (api: GridApi<TData>) => {
      if (!isControlledSelection) return;
      api.forEachNode((node: IRowNode<TData>) => {
        const shouldBeSelected = node.id ? selectedRowsSet.has(node.id) : false;
        if (node.isSelected() !== shouldBeSelected) {
          node.setSelected(shouldBeSelected, false, 'api');
        }
      });
    },
    [isControlledSelection, selectedRowsSet],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) syncSelectionToGrid(api);
  }, [syncSelectionToGrid, data]);

  // Sync after AG Grid finishes processing new row data — the useEffect above
  // can fire before nodes exist; this callback fires after they're ready.
  const handleRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent<TData>) => {
      syncSelectionToGrid(event.api);
      restProps.onRowDataUpdated?.(event);
    },
    // `restProps` is a fresh object every render; depend only on the stable
    // handler reference so this callback stays memoized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [syncSelectionToGrid, restProps.onRowDataUpdated],
  );

  // ── Merge consumer's rowSelection with maxSelections enforcement ──

  const mergedRowSelection = useMemo(() => {
    if (maxSelections == null) return consumerRowSelection;

    const consumerConfig =
      typeof consumerRowSelection === 'object' && consumerRowSelection !== null
        ? consumerRowSelection
        : typeof consumerRowSelection === 'string'
          ? { mode: consumerRowSelection as 'singleRow' | 'multiRow' }
          : {};

    const consumerIsRowSelectable = (consumerConfig as { isRowSelectable?: (node: IRowNode<TData>) => boolean })
      .isRowSelectable;

    return {
      ...consumerConfig,
      isRowSelectable: (node: IRowNode<TData>) => {
        if (consumerIsRowSelectable && !consumerIsRowSelectable(node)) return false;
        if (selectedRowsSet.size >= maxSelections) {
          if (!node.id || !selectedRowsSet.has(node.id)) return false;
        }
        return true;
      },
    } as typeof consumerRowSelection;
  }, [consumerRowSelection, maxSelections, selectedRowsSet]);

  // ── Event handler: enforce maxSelections then forward to consumer ──

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<TData>) => {
      if (event.source === 'api') return;

      if (isControlledSelection && onSelectionChangeProp) {
        let selectedNodes = event.api.getSelectedNodes();

        if (maxSelections != null && selectedNodes.length > maxSelections) {
          const nodesToDeselect = selectedNodes.slice(maxSelections);
          nodesToDeselect.forEach((node: IRowNode<TData>) => node.setSelected(false, false, 'api'));
          selectedNodes = selectedNodes.slice(0, maxSelections);
          onMaxSelectionsReachedProp?.(maxSelections);
        }

        const newIds = selectedNodes
          .map((node: IRowNode<TData>) => node.id)
          .filter((id: string | undefined): id is string => id !== undefined);

        onSelectionChangeProp(newIds);
      }

      consumerOnSelectionChanged?.(event);
    },
    [
      isControlledSelection,
      maxSelections,
      onSelectionChangeProp,
      onMaxSelectionsReachedProp,
      consumerOnSelectionChanged,
    ],
  );

  return (
    <AgGrid<TData>
      ref={gridRef}
      rowData={data}
      columnDefs={columns}
      gridOptions={mergedGridOptions}
      {...restProps}
      // Opt-in: with domLayout="autoHeight" ag-grid sizes the grid to its
      // content, so the root must NOT be forced to h-full (height:100% of a
      // content-height parent) — that would fight autoHeight's inline height and
      // reintroduce a fixed tall viewport. Consumers that set domLayout="autoHeight"
      // (e.g. the ingestion GlobalModulesTable) therefore get h-auto; every other
      // consumer (no domLayout, or 'normal' — including the pivot, which sizes its
      // grid to a measured deterministic height) keeps the default h-full so the
      // grid fills its container.
      domLayout={domLayout}
      rowSelection={mergedRowSelection ?? consumerRowSelection}
      onSelectionChanged={handleSelectionChanged}
      onRowDataUpdated={handleRowDataUpdated}
      className={cn(
        'ui-w-full',
        domLayout === 'autoHeight' ? 'ui-h-auto' : 'ui-h-full',
        dataTableVariants({ size }),
        className,
      )}
    />
  );
}

export type {
  IDataTableV2Props,
  ColDef as IDataTableV2ColDef,
  ICellRendererParams as IDataTableV2CellRendererParams,
  ValueFormatterParams as IDataTableV2ValueFormatterParams,
  GridOptions as IDataTableV2GridOptions,
  ITextFilterParams as IDataTableV2TextFilterParams,
  RowClickedEvent as IDataTableV2RowClickedEvent,
  SelectionChangedEvent as IDataTableV2SelectionChangedEvent,
  GridApi as IDataTableV2GridApi,
  RowDragEndEvent as IDataTableV2RowDragEndEvent,
  RowDragMoveEvent as IDataTableV2RowDragMoveEvent,
  RowDragLeaveEvent as IDataTableV2RowDragLeaveEvent,
};

export { DataTableV2 };
